class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.buttonText = this.submitButton?.querySelector('[data-button-text]');
    this.buttonLoader = this.submitButton?.querySelector('[data-button-loader]');
    this.variantIdInput = this.form.querySelector('input[name="id"]');
    this.maxQuantity = parseInt(this.dataset.maxQuantity) || 5;
    this.originalButtonText = this.buttonText?.textContent.trim();
    this.activeTab = 'colors';

    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    const productTabs = document.querySelector('product-tabs');
    const sameProduct =
      productTabs &&
      this.dataset.productId &&
      productTabs.dataset.productId === this.dataset.productId;

    if (sameProduct) {
      this.productTabs = productTabs;
      document.addEventListener('tab:changed', (event) => this.handleTabChanged(event));

      if (this.productTabs.getActiveTab) {
        this.activeTab = this.productTabs.getActiveTab() || 'colors';
      }
    }

    if (typeof subscribe !== 'undefined') {
      this.unsubscribeCartAdd = subscribe(PUB_SUB_EVENTS.cartAdd, (data) => {
        if (data.optimistic) return;
        this.checkMaxQuantity(data);
      });

      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
        this.checkMaxQuantityFromCart(data.cartData);
      });
    }
  }

  disconnectedCallback() {
    this.unsubscribeCartAdd?.();
    this.unsubscribeCartUpdate?.();
  }

  handleTabChanged(event) {
    if (!event.detail?.activeTab) return;
    this.activeTab = event.detail.activeTab;

    if (this.activeTab === 'colors') {
      delete this.dataset.customizationData;
    } else if (this.activeTab === 'design' && this.productTabs?.getSelectedDesignVariantId) {
      const selectedVariantId = this.productTabs.getSelectedDesignVariantId();
      if (selectedVariantId && event.detail.productId) {
        const storageKey = `customization_${event.detail.productId}_${selectedVariantId}`;
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          this.dataset.customizationData = savedData;
        }
      }
    }
  }

  checkMaxQuantity(data) {
    const variantId = this.variantIdInput?.value;
    if (!variantId || !data.items) return;

    const cartItem = data.items.find((item) => item.variant_id == variantId);
    this.updateButtonForQuantity(cartItem?.quantity ?? 0);
  }

  checkMaxQuantityFromCart(cartData) {
    if (!cartData?.items) return;
    const variantId = this.variantIdInput?.value;
    if (!variantId) return;

    const cartItem = cartData.items.find((item) => item.variant_id == variantId);
    this.updateButtonForQuantity(cartItem?.quantity ?? 0);
  }

  updateButtonForQuantity(quantity) {
    if (quantity >= this.maxQuantity) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
      this.submitButton.classList.remove('hover:bg-gray-800');
      if (this.buttonText) this.buttonText.textContent = `You can buy maximum ${this.maxQuantity} quantity of this product.`;
    } else {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
      this.submitButton.classList.add('hover:bg-gray-800');
      if (this.buttonText) this.buttonText.textContent = this.originalButtonText;
    }
  }

  getDesignTabData() {
    let customizationData = null;
    let selectedDesignVariantId = null;

    if (this.productTabs) {
      selectedDesignVariantId = this.productTabs.getSelectedDesignVariantId();
      if (selectedDesignVariantId) {
        const productId = this.productTabs.dataset.productId;
        if (productId) {
          const storageKey = `customization_${productId}_${selectedDesignVariantId}`;
          const savedData = localStorage.getItem(storageKey);
          if (savedData) {
            try {
              customizationData = JSON.parse(savedData);
            } catch (e) {
              console.error('[ProductForm] Failed to parse customization data:', e);
            }
          }
        }
      }
    }

    if (!customizationData && this.dataset.customizationData) {
      try {
        customizationData = JSON.parse(this.dataset.customizationData);
      } catch (e) {
        console.error('[ProductForm] Failed to parse dataset customization data:', e);
      }
    }

    if (!selectedDesignVariantId || !customizationData) {
      return null;
    }

    const properties = {};
    if (customizationData?.text?.content) {
      const textContent = customizationData.text.content;
      properties['Engraving Text'] = textContent;

      const engravingPrice = this.dataset.engravingPrice;
      if (engravingPrice && engravingPrice !== '0') {
        properties['Engraving Price'] = engravingPrice;
      }
    }

    return {
      variantId: selectedDesignVariantId.toString(),
      properties,
    };
  }

  getCustomizationForVariant(variantId) {
    if (!variantId) return null;
    const productId = this.dataset.productId;
    if (!productId) return null;

    const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
    const storageKey = isSelfMokoPin
      ? `customization_selfpin_${productId}`
      : `customization_${productId}_${variantId}`;

    const savedData = localStorage.getItem(storageKey);
    if (!savedData) return null;

    let customizationData;
    try {
      customizationData = JSON.parse(savedData);
    } catch (e) {
      console.error('[ProductForm] Failed to parse customization data:', e);
      return null;
    }

    const textContent = customizationData?.text?.content;
    if (!textContent) return null;

    const properties = {
      'Engraving Text': textContent,
    };
    const engravingPrice = this.dataset.engravingPrice;
    if (engravingPrice && engravingPrice !== '0') {
      properties['Engraving Price'] = engravingPrice;
    }

    return { variantId: variantId.toString(), properties };
  }

  getMokoPinVariantId() {
    const selected = document.querySelector('[data-moko-pin-container] [data-selected="true"]');
    return selected?.dataset.mokoPinVariantId ?? null;
  }

  getMokoPinVariantData() {
    const pinVariantId = this.getMokoPinVariantId();
    if (!pinVariantId) return null;

    try {
      const variants = JSON.parse(this.dataset.mokoPinVariants || '[]');
      return variants.find((v) => String(v.id) === String(pinVariantId)) ?? null;
    } catch (e) {
      console.error('[ProductForm] Failed to parse moko pin variants JSON:', e);
      return null;
    }
  }

  buildMokoPinItems(designData) {
    const pinVariant = this.getMokoPinVariantData();
    if (!pinVariant) return null;

    const engravingText = designData.properties['Engraving Text'];
    if (!engravingText) return null;

    const parentVariantId = parseInt(designData.variantId, 10);
    const addonValue = `${pinVariant.title} (+ ${pinVariant.price_formatted})`;

    const addonKey = this.dataset.addonLabel || 'Moko Pin';

    return {
      parent: {
        id: parentVariantId,
        quantity: 1,
        properties: {
          [addonKey]: addonValue,
        },
      },
      pin: {
        id: pinVariant.id,
        quantity: 1,
        parent_id: parentVariantId,
        properties: {
          Initials: engravingText,
        },
      },
    };
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitButton?.disabled) return;

    const formData = new FormData(this.form);
    const isMokoPin = this.dataset.hasMokoPin === 'true';
    const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';

    if (isSelfMokoPin) {
      const submitVariantId = this.variantIdInput?.value || this.dataset.variantId;
      const designData =
        this.getCustomizationForVariant(submitVariantId) ?? this.getDesignTabData();
      const hasEngravingText = !!designData?.properties?.['Engraving Text'];

      if (!hasEngravingText) {
        const modal = document.getElementById(`product-customize-modal-${this.dataset.productId}`);
        if (modal && typeof modal.open === 'function') {
          if (submitVariantId && typeof modal.setSelectedMokoPinVariant === 'function') {
            modal.setSelectedMokoPinVariant(submitVariantId);
          }
          modal.open();
        } else {
          const warningMsg = this.dataset.selfPinEngravingWarning || 'Please add engraving text first';
          this.showButtonWarning(warningMsg);
        }
        return;
      }

      formData.set('id', designData.variantId);
      Object.entries(designData.properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });

      this.setLoading(true);
      const sectionId = document.querySelector('cart-drawer')?.dataset?.sectionId;
      const sections = sectionId ? [sectionId] : [];

      try {
        const data = await CartAPI.add(formData, sections);
        publish(PUB_SUB_EVENTS.cartAdd, {
          source: 'product-form',
          productId: this.dataset.productId,
          sections: data.sections,
          items: data.items,
          item_count: data.item_count,
        });
      } catch (error) {
        this.handleAddToCartError(error);
      } finally {
        this.setLoading(false);
      }
      return;
    }

    if (this.activeTab === 'design') {
      const designData = this.getDesignTabData();

      if (isMokoPin) {
        const mokoPinData = designData ? this.buildMokoPinItems(designData) : null;
        if (!mokoPinData) {
          const warningMsg = this.dataset.mokoPinEngravingWarning || 'Please add engraving text first';
          this.showButtonWarning(warningMsg);
          return;
        }

        this.setLoading(true);

        const sectionId = document.querySelector('cart-drawer')?.dataset?.sectionId;
        const sections = sectionId ? [sectionId] : [];

        try {
          const data = await CartAPI.addItems(
            [mokoPinData.parent, mokoPinData.pin],
            sections
          );

          publish(PUB_SUB_EVENTS.cartAdd, {
            source: 'product-form',
            productId: this.dataset.productId,
            sections: data.sections,
            items: data.items,
            item_count: data.item_count,
          });
        } catch (error) {
          this.handleAddToCartError(error);
        } finally {
          this.setLoading(false);
        }
        return;
      }

      if (!designData) {
        console.warn('[ProductForm] No design selected or customization data missing');
        return;
      }

      formData.set('id', designData.variantId);
      Object.entries(designData.properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    } else {
      delete this.dataset.customizationData;
    }

    this.setLoading(true);

    const sectionId = document.querySelector('cart-drawer')?.dataset?.sectionId;
    const sections = sectionId ? [sectionId] : [];

    try {
      const data = await CartAPI.add(formData, sections);

      publish(PUB_SUB_EVENTS.cartAdd, {
        source: 'product-form',
        productId: this.dataset.productId,
        sections: data.sections,
        items: data.items,
        item_count: data.item_count,
      });
    } catch (error) {
      this.handleAddToCartError(error);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;

    if (this.buttonText) this.buttonText.classList.toggle('invisible', isLoading);
    if (this.buttonLoader) {
      this.buttonLoader.classList.toggle('hidden', !isLoading);
      this.buttonLoader.classList.toggle('flex', isLoading);
    }
  }

  handleAddToCartError(error) {
    const isMaxQuantityError = error?.status === 422;

    if (isMaxQuantityError) {
      const message = error?.description ?? error?.message ?? `You can buy maximum ${this.maxQuantity} quantity of this product.`;
      this.submitButton.disabled = true;
      this.submitButton.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none', 'animate-shake');
      this.submitButton.classList.remove('hover:bg-gray-800');
      if (this.buttonText) this.buttonText.textContent = message;
      navigator.vibrate?.(100);
      setTimeout(() => this.submitButton?.classList.remove('animate-shake'), 600);
    } else {
      const message = error?.description ?? error?.message ?? 'Something went wrong.';
      this.showButtonWarning(message);
    }

    publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', error });
  }

  showButtonWarning(message) {
    if (!this.buttonText || !this.submitButton) return;

    const original = this.buttonText.textContent.trim();
    this.buttonText.textContent = message;
    this.submitButton.classList.add('animate-shake');
    navigator.vibrate?.(100);

    setTimeout(() => {
      this.buttonText.textContent = original;
      this.submitButton.classList.remove('animate-shake');
    }, 2500);
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
